import { uniq } from 'es-toolkit';

import { Insertion, insertionsEqual, ModelCategory, OrganizationMaterialSet, User } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { getLoggedInUsersOrganizationMaterialSet } from '@paragon/material-lists';
import { enqueueError } from '@paragon/notification-tools';

import { getInsertionsWithPendingChanges, getLayoutModelsWithPendingChanges } from '../selectors/changeTracker';
import { getChildrenFinder } from '../selectors/ChildrenFinder';
import { getDescendantGuids } from '../selectors/getDescendants';
import { LayoutModelsLibraryThunkAction } from '../store';
import { insertionSetQuantity, modelPropertyChanged } from '../store/changeTracker';
import { projectArchived } from '../store/layoutModelsSlice';
import { saveChangesForSingleProject } from './saveActions';

export const getLoggedInUserAndOrganizationMaterialSet =
  (): LayoutModelsLibraryThunkAction<{
    loggedInUser: User | null;
    materialSet: OrganizationMaterialSet | null;
  }> =>
  (dispatch, getState) => {
    const { loggedInUser } = getState().logInData;
    if (!loggedInUser) {
      console.error('You must be logged in.');
    }

    const materialSet = getLoggedInUsersOrganizationMaterialSet(getState());
    if (!materialSet) {
      dispatch(
        enqueueError(
          'Material lists have not been loaded yet. Please try again later or contact Paragon Customer Support.',
        ),
      );
    }

    return { loggedInUser, materialSet };
  };

export const editLayoutModelProperties =
  (layoutModelGuid: string, editedProperties: Record<string, any>): LayoutModelsLibraryThunkAction<void> =>
  (dispatch, getState) => {
    const state = getState();

    const layoutModel = getLayoutModelsWithPendingChanges(state)[layoutModelGuid];
    if (!layoutModel) {
      throw new Error('Modified model could not be found.');
    }
    if (layoutModel.category !== ModelCategory.Project && layoutModel.category !== ModelCategory.IntermediateModel) {
      throw new Error('Only layout models can be edited by this method.');
    }

    for (const [propertyName, newValue] of Object.entries(editedProperties)) {
      dispatch(
        modelPropertyChanged({
          modelGuids: [layoutModelGuid],

          propertyName,
          newValue,
        }),
      );
    }
  };

export const changeInsertionQuantity =
  (insertion: Insertion, requestedChange: number): LayoutModelsLibraryThunkAction<void> =>
  (dispatch, getState) => {
    const insertions = getInsertionsWithPendingChanges(getState());
    const oldQuantity = insertions.filter((stateInsertion) => insertionsEqual(stateInsertion, insertion)).length;

    // ensure final quantity does not go below 0
    const change = Math.max(requestedChange, -oldQuantity);

    return dispatch(insertionSetQuantity({ insertion, change }));
  };

export const archiveProject =
  (projectGuid: string): LayoutModelsLibraryThunkAction<void> =>
  async (dispatch, getState) => {
    await dispatch(saveChangesForSingleProject(projectGuid));

    await designService.projects.archive(projectGuid);

    const descendants = uniq(getDescendantGuids(projectGuid, getChildrenFinder(getState())));
    dispatch(projectArchived({ guid: projectGuid, descendants }));
  };
